<template>
  <div
    v-for="({ mention_link, user_name, user_email, sentence }, idx) in mentions"
    :key="activity?.uuid + '__' + idx"
    class="gong-call-context"
  >
    <div
      class="mention-info"
      v-if="mention_link && (user_name || user_email) && sentence"
    >
      <span class="text-neutral-text-strong">{{
        user_name || user_email
      }}</span>
      <span>•</span>
      <button
        class="text-secondary-text"
        target="_blank"
        @click="onClickDeepLink(mention_link)"
        type="button"
      >
        {{ buildTimestamp(mention_link) }}
        <FontAwesomeIcon
          :icon="['fak', 'open-link']"
          :style="{ height: '12px', width: '12px', color: 'currentColor' }"
          class="text-secondary-text"
        />
      </button>
    </div>
    <p
      class="text-neutral-text"
      data-testid="mention-sentence"
      v-html="buildHighlightedSentence(sentence)"
    />
  </div>
</template>

<script setup lang="ts">
import { ATTRIBUTION_CTAS, EVENT_SITES } from '@crossbeam/itly';
import { highlightCompanyNameInSentence } from '@crossbeam/utils';

import { Duration } from 'luxon';
import { computed } from 'vue';

import useIteratively from '@/composables/useIteratively';
import { usePartnersStore } from '@/stores';
import { Partner } from '@/types/partners';
import { TimelineEvent } from '@/types/timeline';

const { activity } = defineProps<{
  activity?: TimelineEvent;
}>();

const partnerStore = usePartnersStore();
const partner = computed(
  () =>
    partnerStore.getPartnerOrgByUuid(
      activity?.partner_organization_uuid ?? '',
    ) as Partner,
);

const mentions = computed(
  () =>
    (activity?.activity_source === 'gong' && 'mentions' in activity.data
      ? activity.data.mentions
      : []) as unknown as {
      mention_link: string;
      user_name: string;
      user_email: string;
      sentence: string;
    }[],
);

function buildTimestamp(link?: string) {
  const highlights = new URLSearchParams(link).get('highlights') ?? '';
  const seconds = JSON.parse(decodeURIComponent(highlights))?.at(-1)?.from;
  if (!seconds) return '--:--';

  const duration = Duration.fromObject({ seconds });

  if (duration.seconds > 3600) return duration.toFormat('hh:mm:ss');
  return duration?.toFormat('mm:ss');
}

function buildHighlightedSentence(sentence: string) {
  return highlightCompanyNameInSentence(sentence, partner.value.name);
}

const { iteratively } = useIteratively();

function onClickDeepLink(url: string) {
  iteratively.userInteractedWithTimeline({
    cta: ATTRIBUTION_CTAS.GONG_MENTIONS,
    cta_2: partner.value.name,
    event_site: EVENT_SITES.GONG_MENTIONS,
  });

  window.open(url, '_blank');
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.gong-call-context {
  @apply text-sm text-neutral-text mt-16 ml-18;
}

.mention-info {
  @apply flex items-center gap-4 mb-4;
}

:deep() .highlight-me {
  @apply bg-info-background-weak px-4 rounded-lg font-bold text-info-text-strong;
}
</style>
